import { useState } from "react";
import { motion } from "framer-motion";
import { Play } from "lucide-react";

/**
 * "Why a World" — the Site Info band, directly above the footer.
 *
 * Tuesday: paste the founder's film URL into VIDEO_SRC and the frame appears.
 * An .mp4/.webm renders as a real <video>; anything else is treated as an embed
 * URL (YouTube / Vimeo). Empty means the section stands on the words alone, so
 * the page is never advertising something that is not there yet.
 *
 * The words and the film tell the SAME story in the same order — problem, why
 * nothing fixed it, the door, the stake, the artifact. Script:
 * /app/memory/SITE_INFO_VIDEO_SCRIPT.md
 */
const VIDEO_SRC = "";
const VIDEO_POSTER = "/brand/og-cover.jpg";

const BEATS = [
  {
    n: "01",
    head: "There is nowhere to go and do it",
    body:
      "There is a thing you have been meaning to do. Not a task — the big one. You have thought about it for years and you have not started it. That is not a discipline problem. It is that nowhere in your life is there anywhere to go and do it.",
  },
  {
    n: "02",
    head: "A list is where an ambition goes to be stored",
    body:
      "We have been handed checklists, dashboards, and now a chat window happy to talk about it forever, that remembers nothing and never once asks you to commit. None of it costs you anything, so none of it counts.",
  },
  {
    n: "03",
    head: "So we built somewhere to go",
    body:
      "Endeavorum is a world you walk into. In the Arcade you sit down with MelzAI and settle what you are actually doing. Then you cross into Clarity Hall and work out how. Two buildings, because deciding and doing are two different days — and it is the same you in both.",
  },
  {
    n: "04",
    head: "You ante on yourself",
    body:
      "It will not let you bet on \u201cpublish the book\u201d — that is a decade, not a plan. It makes you cut one finishable piece and put a date on it. Then you stake your own tokens on it, and you watch the balance drop.",
  },
  {
    n: "05",
    head: "You leave holding a blueprint",
    body:
      "You empty your head onto a wall. It gets carried, sorted, ordered, and laid across the days you actually have. You leave with something dated and sequenced — and exportable to plain text, because your words are not our property.",
  },
];

const isFile = /\.(mp4|webm|mov)(\?|$)/i.test(VIDEO_SRC);

export const WhyAWorld = () => {
  const [playing, setPlaying] = useState(false);

  return (
    <section
      className="mx-auto max-w-[1500px] px-6 pb-4 pt-16 md:px-10"
      data-testid="why-a-world"
    >
      <div className="mb-10 max-w-2xl border-t border-white/12 pt-10">
        <p className="tracked ink-mid mb-3 text-[10px] font-semibold">Why a World</p>
        <h2 className="serif ink text-3xl font-light leading-[1.1] sm:text-4xl">
          Everyone has the one thing they never started.
          <span className="italic text-[var(--stage-gold)]"> We built it somewhere to happen.</span>
        </h2>
      </div>

      <div
        className={
          VIDEO_SRC
            ? "grid gap-10 lg:grid-cols-[1fr_460px] lg:gap-14"
            : "grid gap-10"
        }
      >
        {/* ---- the story, in the film's own order */}
        <ol className="grid gap-x-14 gap-y-0 sm:grid-cols-2" data-testid="why-beats">
          {BEATS.map((b, i) => (
            <motion.li
              key={b.n}
              className="flex items-start gap-4 border-t border-white/10 py-6 first:border-t-0 sm:first:border-t sm:[&:nth-child(-n+2)]:border-t-0"
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              data-testid="why-beat"
            >
              <span className="mono mt-1 shrink-0 text-[13px] text-[var(--stage-gold)]/70">
                {b.n}
              </span>
              <div className="min-w-0">
                <h3 className="serif ink text-[19px] font-normal leading-tight">{b.head}</h3>
                <p className="ink-mid mt-2 text-[14px] leading-relaxed">{b.body}</p>
              </div>
            </motion.li>
          ))}
        </ol>

        {/* ---- the film, once it exists */}
        {VIDEO_SRC && (
          <div className="lg:pt-1" data-testid="why-film">
            <div className="parchment overflow-hidden rounded-xl">
              <div className="relative aspect-video bg-[#0b0a08]">
                {isFile ? (
                  <video
                    className="h-full w-full object-cover"
                    src={VIDEO_SRC}
                    poster={VIDEO_POSTER}
                    controls={playing}
                    playsInline
                    preload="metadata"
                    data-testid="why-film-video"
                  />
                ) : (
                  <iframe
                    className="h-full w-full"
                    src={VIDEO_SRC}
                    title="Why a World"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture"
                    allowFullScreen
                    data-testid="why-film-embed"
                  />
                )}

                {isFile && !playing && (
                  <button
                    type="button"
                    data-testid="why-film-play"
                    onClick={(e) => {
                      setPlaying(true);
                      const v = e.currentTarget.parentElement?.querySelector("video");
                      v?.play();
                    }}
                    className="group absolute inset-0 flex items-center justify-center bg-black/25 transition-colors hover:bg-black/10"
                    aria-label="Play the film"
                  >
                    <span className="flex h-16 w-16 items-center justify-center rounded-full border border-[var(--stage-gold)]/60 bg-black/50 backdrop-blur-sm transition-transform duration-300 group-hover:scale-110">
                      <Play size={22} strokeWidth={1.8} className="ml-0.5 text-[#f6d696]" fill="#f6d696" />
                    </span>
                  </button>
                )}
              </div>
              <div className="flex items-baseline justify-between px-4 py-3">
                <span className="serif ink text-[15px]">Why a World</span>
                <span className="mono text-[10px] opacity-50" style={{ color: "inherit" }}>
                  90 SEC
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default WhyAWorld;
